export type FileType = 'pdf' | 'doc' | 'docx' | 'txt' | 'xlsx' | 'xls';

/**
 * 工具类
 */
export class Utils {
    // 事件类型
    private static eventsType = [
        'onActivate',
        'onAddUndo',
        'onBeforeAddUndo',
        'onBeforeExecCommand',
        'onBeforeGetContent',
        'onBeforeRenderUI',
        'onBeforeSetContent',
        'onBeforePaste',
        'onBlur',
        'onChange',
        'onClearUndos',
        'onClick',
        'onContextMenu',
        'onCopy',
        'onCut',
        'onDblclick',
        'onDeactivate',
        'onDirty',
        'onDrag',
        'onDragDrop',
        'onDragEnd',
        'onDragGesture',
        'onDragOver',
        'onDrop',
        'onExecCommand',
        'onFocus',
        'onFocusIn',
        'onFocusOut',
        'onGetContent',
        'onHide',
        'onInit',
        'onKeyDown',
        'onKeyPress',
        'onKeyUp',
        'onLoadContent',
        'onMouseDown',
        'onMouseEnter',
        'onMouseLeave',
        'onMouseMove',
        'onMouseOut',
        'onMouseOver',
        'onMouseUp',
        'onNodeChange',
        'onObjectResizeStart',
        'onObjectResized',
        'onObjectSelected',
        'onPaste',
        'onPostProcess',
        'onPostRender',
        'onPreProcess',
        'onProgressState',
        'onRedo',
        'onRemove',
        'onReset',
        'onSaveContent',
        'onSelectionChange',
        'onSetAttrib',
        'onSetContent',
        'onShow',
        'onSubmit',
        'onUndo',
        'onVisualAid',
    ];
    // 文件类型
    private static fileType: { [key in FileType]: string } = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        txt: 'text/plain',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    // 唯一id索引
    private unique: number = 0;

    /**
     * 计算字符长度
     * @param {string} str
     * @param {string} suffix
     * @returns {boolean}
     */
    endsWith = (str: string, suffix: string) => str.indexOf(suffix, str.length - suffix.length) !== -1;

    /**
     * file type transform
     */
    getFileTypes = (keys: Array<FileType>) => keys.map((key: FileType) => Utils.fileType[key]).join(',');

    /**
     * 生成唯一id
     * @param {string} prefix
     * @returns {string}
     */
    uuid = (prefix: string): string => {
        const date = new Date();
        const time = date.getTime();
        const random = Math.floor(Math.random() * 1000000000);

        this.unique++;
        return `${prefix}_${random}${this.unique}${String(time)}`;
    };

    /**
     * 是否为可接受的文件类型
     * @param {File} file
     * @param {string | Array<string>} acceptedFiles
     * @returns {boolean}
     */
    isAcceptable = (file: File, acceptedFiles: string | Array<FileType>) => {
        if (file && acceptedFiles) {
            const acceptedFilesArray = Array.isArray(acceptedFiles) ? acceptedFiles : acceptedFiles.split(',');
            const fileName = file.name || '';
            const mimeType = file.type || '';
            const baseMimeType = mimeType.replace(/\/.*$/, '');

            return acceptedFilesArray.some((type: FileType) => {
                const validType = Utils.fileType[type.trim() as FileType]; // 文件类型转化
                if (validType.charAt(0) === '.') {
                    return this.endsWith(fileName.toLowerCase(), validType.toLowerCase());
                } else if (/\/\*$/.test(validType)) {
                    // This is something like a image/* mime type
                    return baseMimeType === validType.replace(/\/.*$/, '');
                }
                return mimeType === validType;
            });
        }
        return true;
    };

    /**
     * 是否为文本框
     * @param {Element | null} element
     * @returns {element is HTMLTextAreaElement}
     */
    isTextarea = (element: Element | null): element is HTMLTextAreaElement => {
        return element !== null && element.tagName.toLowerCase() === 'textarea';
    };

    /**
     * 是否为函数
     * @param x
     * @returns {x is Function}
     */
    isFunction = (x: any): x is Function => typeof x === 'function';

    /**
     * 是否为合法事件
     * @param {string[]} keys
     * @returns {(key: string) => boolean}
     */
    isValidKey = (keys: string[]) => (key: string) => keys.indexOf(key) !== -1;

    /**
     * 事件绑定控制
     * @param props
     * @param editor
     * @param {Event} initEvent
     */
    bindHandlers = (props: any, editor: any, initEvent: Event): void => {
        Object.keys(props)
            .filter(this.isValidKey(Utils.eventsType))
            .forEach((key: string) => {
                const handler = props[key];
                if (this.isFunction(handler)) {
                    if (key === 'onInit') {
                        handler(initEvent, editor);
                    } else {
                        editor.on(key.substring(2), (e: any) => handler(e, editor));
                    }
                }
            });
    };

    /**
     * 过滤plugins
     * @param {string | string[]} plugins
     * @returns {string[]}
     */
    normalizePluginArray = (plugins?: string | string[]): string[] => {
        if (typeof plugins === 'undefined' || plugins === '') {
            return [];
        }
        return Array.isArray(plugins) ? plugins : plugins.split(' ');
    };

    /**
     * 合并plugins
     * @param {string | string[]} initPlugins
     * @param {string | string[]} inputPlugins
     * @returns {string[]}
     */
    mergePlugins = (initPlugins: string | string[], inputPlugins?: string | string[]) => {
        return this.normalizePluginArray(initPlugins).concat(this.normalizePluginArray(inputPlugins));
    };
}

/**
 * 获取全局环境
 * @returns {any}
 */
export const getGlobal = (): any => (typeof window !== 'undefined' ? window : global);

/**
 * 获取tinymce实例
 * @returns {null}
 */
export const getTinymce = () => {
    const global = getGlobal();
    return global && global.tinymce ? global.tinymce : null;
};

export default new Utils();
